import React, { useEffect, useState, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Home, CheckCircle, ArrowRight } from 'lucide-react';
import { API_BASE } from '../config';
import { useTranslation } from '../i18n/LanguageContext';
import { useAuth } from '../App';

const BillingSuccess = ({ onNavigate }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { onRefetchBilling } = useOutletContext() || {};
  const [sessionDetails, setSessionDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const redirectDone = useRef(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    if (sessionId) {
      const token = localStorage.getItem('token');
      if (token) {
        fetch(`${API_BASE}/api/billing/verify-session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ session_id: sessionId })
        })
          .then(r => r.ok ? r.json() : null)
          .then(data => {
            setSessionDetails({
              sessionId,
              plan: data?.planName || 'Subscription',
              amount: '—',
              employees: '—',
              status: 'success'
            });
            if (onRefetchBilling) onRefetchBilling();
            // Second refetch after a short delay so backend/webhook state is sure to be reflected (trial screen goes away)
            setTimeout(() => { if (onRefetchBilling) onRefetchBilling(); }, 600);
          })
          .catch(() => {
            setSessionDetails({ sessionId, plan: 'Subscription', amount: '—', employees: '—', status: 'success' });
            if (onRefetchBilling) onRefetchBilling();
          })
          .finally(() => {
            setLoading(false);
            if (onRefetchBilling) onRefetchBilling();
          });
      } else {
        setSessionDetails({ sessionId, plan: 'Subscription', amount: '—', employees: '—', status: 'success' });
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, [onRefetchBilling]);

  // Auto-redirect to dashboard after success so user goes direct to dashboard (not stuck on success screen)
  useEffect(() => {
    if (!sessionDetails || loading) return;
    const t = setTimeout(() => {
      if (redirectDone.current) return;
      redirectDone.current = true;
      const isAdminOrManager = user?.role === 'admin' || user?.role === 'manager';
      const target = isAdminOrManager ? 'dashboard' : 'home';
      if (onNavigate) onNavigate(target);
      else window.location.href = isAdminOrManager ? '/dashboard' : '/home';
    }, 2500);
    return () => clearTimeout(t);
  }, [sessionDetails, loading, onNavigate, user?.role]);

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        width: '100%',
        background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)',
        color: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px'
        }}>
          <div style={{
            border: '4px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '50%',
            borderTop: '4px solid #3b82f6',
            width: '50px',
            height: '50px',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{ color: 'rgba(255, 255, 255, 0.8)' }}>Vérification de votre paiement...</p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      width: '100%',
      background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)',
      color: '#ffffff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
      overflowX: 'hidden',
      paddingBottom: 'env(safe-area-inset-bottom, 0)',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflowY: 'auto'
    }}>
      {/* Header transparent */}
      {onNavigate && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          padding: '24px 20px 20px',
          background: 'transparent',
          backdropFilter: 'none',
          borderBottom: 'none',
          zIndex: 999,
          maxWidth: '100%'
        }}>
          <button
            onClick={() => onNavigate('home')}
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              color: '#ffffff',
              padding: '10px',
              borderRadius: '12px',
              fontSize: '18px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s',
              backdropFilter: 'blur(10px)',
              width: '44px',
              height: '44px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
              e.currentTarget.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              e.currentTarget.style.transform = 'scale(1)';
            }}
            title={t('billing.back_to_home')}
          >
            <Home size={20} />
          </button>
        </div>
      )}

      {/* Contenu principal */}
      <div className="success-content" style={{
        paddingTop: '100px',
        paddingBottom: '40px',
        paddingLeft: '20px',
        paddingRight: '20px',
        maxWidth: '800px',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center'
      }}>
      <div style={{
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '20px',
        padding: '40px',
        marginBottom: '32px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)',
        width: '100%'
      }}>
        <div style={{
          marginBottom: '24px'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #10b981, #059669)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            boxShadow: '0 8px 24px rgba(16, 185, 129, 0.4)'
          }}>
            <CheckCircle size={48} color="#ffffff" />
          </div>
          <h1 style={{
            color: '#ffffff',
            fontSize: '32px',
            fontWeight: 700,
            marginBottom: '12px'
          }}>
            {t('billing.payment_success')}
          </h1>
          <p style={{
            color: 'rgba(255, 255, 255, 0.8)',
            fontSize: '16px'
          }}>
            {t('billing.payment_success_msg')}
          </p>
        </div>

        {sessionDetails && (
          <div style={{
            background: 'rgba(255, 255, 255, 0.03)',
            borderRadius: '16px',
            padding: '24px',
            marginTop: '24px'
          }}>
            <h2 style={{
              color: '#ffffff',
              fontSize: '20px',
              fontWeight: 700,
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              📋 {t('billing.subscription_details')}
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px'
            }}>
              <div style={{
                padding: '12px',
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '12px'
              }}>
                <span style={{
                  display: 'block',
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '13px',
                  marginBottom: '4px'
                }}>
                  Plan
                </span>
                <span style={{
                  color: '#ffffff',
                  fontSize: '16px',
                  fontWeight: 600
                }}>
                  {sessionDetails.plan}
                </span>
              </div>
              <div style={{
                padding: '12px',
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '12px'
              }}>
                <span style={{
                  display: 'block',
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '13px',
                  marginBottom: '4px'
                }}>
                  {t('billing.amount')}
                </span>
                <span style={{
                  color: '#ffffff',
                  fontSize: '16px',
                  fontWeight: 600
                }}>
                  {sessionDetails.amount}{t('billing.per_month')}
                </span>
              </div>
              <div style={{
                padding: '12px',
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '12px'
              }}>
                <span style={{
                  display: 'block',
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '13px',
                  marginBottom: '4px'
                }}>
                  {t('billing.employees_included')}
                </span>
                <span style={{
                  color: '#ffffff',
                  fontSize: '16px',
                  fontWeight: 600
                }}>
                  {sessionDetails.employees}
                </span>
              </div>
              <div style={{
                padding: '12px',
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '12px'
              }}>
                <span style={{
                  display: 'block',
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '13px',
                  marginBottom: '4px'
                }}>
                  Session ID
                </span>
                <span style={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontSize: '12px',
                  fontFamily: 'monospace'
                }}>
                  {sessionDetails.sessionId.substring(0, 20)}...
                </span>
              </div>
            </div>
          </div>
        )}

        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '20px',
          padding: '32px',
          marginTop: '24px',
          width: '100%'
        }}>
          <h2 style={{
            color: '#ffffff',
            fontSize: '24px',
            fontWeight: 700,
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            🚀 Prochaines étapes
          </h2>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '20px'
          }}>
            <div style={{
              display: 'flex',
              gap: '16px',
              alignItems: 'flex-start'
            }}>
              <div style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                fontWeight: 700,
                fontSize: '16px'
              }}>
                1
              </div>
              <div>
                <h3 style={{
                  color: '#ffffff',
                  fontSize: '18px',
                  fontWeight: 600,
                  marginBottom: '8px'
                }}>
                  Accédez à votre dashboard
                </h3>
                <p style={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '14px',
                  margin: 0
                }}>
                  Votre compte est maintenant premium avec toutes les fonctionnalités débloquées
                </p>
              </div>
            </div>
            <div style={{
              display: 'flex',
              gap: '16px',
              alignItems: 'flex-start'
            }}>
              <div style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #10b981, #059669)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                fontWeight: 700,
                fontSize: '16px'
              }}>
                2
              </div>
              <div>
                <h3 style={{
                  color: '#ffffff',
                  fontSize: '18px',
                  fontWeight: 600,
                  marginBottom: '8px'
                }}>
                  Ajoutez vos employés
                </h3>
                <p style={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '14px',
                  margin: 0
                }}>
                  Invitez votre équipe et commencez le pointage digital
                </p>
              </div>
            </div>
            <div style={{
              display: 'flex',
              gap: '16px',
              alignItems: 'flex-start'
            }}>
              <div style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                fontWeight: 700,
                fontSize: '16px'
              }}>
                3
              </div>
              <div>
                <h3 style={{
                  color: '#ffffff',
                  fontSize: '18px',
                  fontWeight: 600,
                  marginBottom: '8px'
                }}>
                  Configurez votre espace
                </h3>
                <p style={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '14px',
                  margin: 0
                }}>
                  Personnalisez les paramètres selon vos besoins
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CSS pour animations */}
      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @media (min-width: 768px) {
          .success-content {
            padding: 120px 40px 40px !important;
          }
        }
        @media (min-width: 1024px) {
          .success-content {
            padding: 140px 60px 60px !important;
          }
        }
        .success-content {
          animation: slideUp 0.4s ease-out;
        }
      `}</style>

        <div className="actions">
          <button 
            onClick={() => (onNavigate ? onNavigate('dashboard') : (window.location.href = '/dashboard'))}
            className="primary-btn"
          >
            🎯 Accéder au Dashboard
          </button>
          <button 
            onClick={() => (onNavigate ? onNavigate('billing') : (window.location.href = '/facturation'))}
            className="secondary-btn"
          >
            📊 {t('billing.manage_subscription')}
          </button>
        </div>

        <div className="support-info">
          <h3>🤝 Besoin d'aide ?</h3>
          <p>Notre équipe est là pour vous accompagner :</p>
          <div className="contact-options">
            <span>📧 support@workbiflow.com</span>
            <span>📞 +33 1 23 45 67 89</span>
            <span>💬 Chat en direct</span>
          </div>
        </div>
      </div>

      <style>{`
        .success-page {
          min-height: 100vh;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2rem;
        }

        .success-container {
          background: white;
          border-radius: 20px;
          padding: 3rem;
          max-width: 600px;
          width: 100%;
          box-shadow: 0 20px 40px rgba(0,0,0,0.1);
          text-align: center;
        }

        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
          color: white;
          padding: 4rem;
        }

        .spinner {
          border: 4px solid rgba(255,255,255,0.3);
          border-radius: 50%;
          border-top: 4px solid white;
          width: 50px;
          height: 50px;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .success-header {
          margin-bottom: 3rem;
        }

        .success-icon {
          font-size: 4rem;
          margin-bottom: 1rem;
          animation: bounce 2s infinite;
        }

        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% {
            transform: translateY(0);
          }
          40% {
            transform: translateY(-10px);
          }
          60% {
            transform: translateY(-5px);
          }
        }

        .success-header h1 {
          font-size: 2.5rem;
          color: #2c3e50;
          margin-bottom: 1rem;
        }

        .success-header p {
          font-size: 1.2rem;
          color: #666;
        }

        .payment-details {
          background: #f8f9fa;
          border-radius: 12px;
          padding: 2rem;
          margin-bottom: 3rem;
          text-align: left;
        }

        .payment-details h2 {
          text-align: center;
          margin-bottom: 1.5rem;
          color: #2c3e50;
        }

        .details-grid {
          display: grid;
          gap: 1rem;
        }

        .detail-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.75rem 0;
          border-bottom: 1px solid #eee;
        }

        .detail-item:last-child {
          border-bottom: none;
        }

        .label {
          font-weight: 600;
          color: #555;
        }

        .value {
          font-weight: 700;
          color: #2c3e50;
        }

        .session-id {
          font-family: monospace;
          font-size: 0.9rem;
        }

        .next-steps {
          margin-bottom: 3rem;
          text-align: left;
        }

        .next-steps h2 {
          text-align: center;
          margin-bottom: 2rem;
          color: #2c3e50;
        }

        .steps-list {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .step {
          display: flex;
          align-items: flex-start;
          gap: 1rem;
        }

        .step-number {
          background: linear-gradient(45deg, #4CAF50, #45a049);
          color: white;
          width: 30px;
          height: 30px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          flex-shrink: 0;
        }

        .step-content h3 {
          margin: 0 0 0.5rem 0;
          color: #2c3e50;
        }

        .step-content p {
          margin: 0;
          color: #666;
        }

        .actions {
          display: flex;
          gap: 1rem;
          margin-bottom: 3rem;
          flex-wrap: wrap;
        }

        .primary-btn, .secondary-btn {
          flex: 1;
          padding: 1rem 2rem;
          border: none;
          border-radius: 8px;
          font-size: 1.1rem;
          font-weight: bold;
          cursor: pointer;
          transition: all 0.3s ease;
          min-width: 200px;
        }

        .primary-btn {
          background: linear-gradient(45deg, #4CAF50, #45a049);
          color: white;
        }

        .primary-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(76, 175, 80, 0.4);
        }

        .secondary-btn {
          background: linear-gradient(45deg, #2196F3, #1976D2);
          color: white;
        }

        .secondary-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(33, 150, 243, 0.4);
        }

        .support-info {
          background: #f8f9fa;
          border-radius: 12px;
          padding: 2rem;
        }

        .support-info h3 {
          margin-bottom: 1rem;
          color: #2c3e50;
        }

        .support-info p {
          margin-bottom: 1rem;
          color: #666;
        }

        .contact-options {
          display: flex;
          gap: 2rem;
          justify-content: center;
          flex-wrap: wrap;
        }

        .contact-options span {
          background: white;
          padding: 0.5rem 1rem;
          border-radius: 20px;
          color: #2c3e50;
          font-weight: 500;
          border: 2px solid #e0e0e0;
        }

        @media (max-width: 768px) {
          .success-container {
            padding: 2rem;
            margin: 1rem;
          }

          .actions {
            flex-direction: column;
          }

          .contact-options {
            flex-direction: column;
            gap: 1rem;
          }
        }
      `}</style>
    </div>
  );
};

export default BillingSuccess;